import React, { useState, useEffect, useRef } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

export default function LiveCheckInFeed({ branchId }) {
  useAuth();
  const [checkIns, setCheckIns] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newEntryId, setNewEntryId] = useState(null);
  const feedRef = useRef(null);

  const fetchTodayCheckIns = async () => {
    try {
      const branchParam = branchId ? { branch_id: branchId } : {};
      const { data: attendData } = await api.get('/attendances/today', { params: { ...branchParam } });

      // Filter client-side by branch_id as a safety net when API doesn't filter
      const scoped = (attendData || [])?.filter(a => !branchId || a?.branch_id === branchId);

      const enriched = scoped?.map(a => ({
        id: a?.id,
        memberId: a?.member_id,
        memberName: a?.member_name || "Unknown Member",
        trainerName: "Unassigned",
        checkInTime: a?.check_in_time,
      }));

      // Unique by member_id (keep latest)
      const seen = new Set();
      const unique = enriched?.filter(e => {
        if (seen?.has(e?.memberId)) return false;
        seen?.add(e?.memberId);
        return true;
      });

      setCheckIns(unique);
      setTotalCount(unique?.length);
    } catch (err) {
      console.error("Error fetching check-ins:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayCheckIns();
  }, [branchId]);

  const formatTime = (isoString) => {
    if (!isoString) return "--";
    return new Date(isoString)?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const todayLabel = new Date()?.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="bg-[var(--color-card)] rounded-[var(--radius-lg)] border border-[var(--color-border)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Icon name="Activity" size={16} color="#059669" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
              Live Check-in Feed
            </h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              {todayLabel}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Live indicator */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium text-emerald-700 font-[var(--font-caption)]">Live</span>
          </div>
          {/* Today's count badge */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 rounded-full">
            <Icon name="Users" size={12} color="var(--color-primary)" />
            <span className="text-xs font-bold text-[var(--color-primary)] font-[var(--font-heading)]">
              {loading ? "..." : totalCount}
            </span>
            <span className="text-xs text-[var(--color-primary)]/70 font-[var(--font-caption)]">today</span>
          </div>
          <button
            onClick={fetchTodayCheckIns}
            className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors"
            title="Refresh"
          >
            <Icon name="RefreshCw" size={13} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>

      {/* Feed Body */}
      <div ref={feedRef} className="overflow-y-auto" style={{ maxHeight: "320px" }}>
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4]?.map(i => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-[var(--color-muted)] flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-[var(--color-muted)] rounded w-1/3" />
                  <div className="h-2.5 bg-[var(--color-muted)] rounded w-1/2" />
                </div>
                <div className="h-3 bg-[var(--color-muted)] rounded w-14" />
              </div>
            ))}
          </div>
        ) : checkIns?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-3">
              <Icon name="UserCheck" size={20} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-heading)]">
              No check-ins yet today
            </p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
              New check-ins will appear here in real-time
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--color-border)]">
            {/* Column header */}
            <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-[var(--color-muted)]/30">
              <div className="col-span-5 text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wide">
                Member
              </div>
              <div className="col-span-4 text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wide">
                Trainer
              </div>
              <div className="col-span-3 text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wide text-right">
                Time
              </div>
            </div>
            {checkIns?.map((entry, idx) => (
              <div
                key={entry?.id}
                className={`grid grid-cols-12 gap-2 items-center px-5 py-3 transition-all duration-500 ${
                  newEntryId === entry?.id
                    ? "bg-emerald-50 border-l-2 border-emerald-400" :"hover:bg-[var(--color-muted)]/20"
                }`}
              >
                {/* Member */}
                <div className="col-span-5 flex items-center gap-2.5 min-w-0">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                    style={{
                      backgroundColor: `hsl(${(entry?.memberName?.charCodeAt(0) || 65) * 5 % 360}, 60%, 50%)`,
                    }}
                  >
                    {entry?.memberName?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-body)] truncate">
                      {entry?.memberName}
                    </p>
                    {newEntryId === entry?.id && (
                      <span className="text-xs text-emerald-600 font-[var(--font-caption)]">Just checked in</span>
                    )}
                  </div>
                </div>

                {/* Trainer */}
                <div className="col-span-4 min-w-0">
                  {entry?.trainerName === "Unassigned" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--color-muted)] text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      <Icon name="UserMinus" size={10} color="var(--color-muted-foreground)" />
                      Unassigned
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-xs text-blue-700 font-[var(--font-caption)] truncate max-w-full">
                      <Icon name="User" size={10} color="#1d4ed8" />
                      <span className="truncate">{entry?.trainerName}</span>
                    </span>
                  )}
                </div>

                {/* Time */}
                <div className="col-span-3 text-right">
                  <span className="text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] tabular-nums">
                    {formatTime(entry?.checkInTime)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer summary */}
      {!loading && checkIns?.length > 0 && (
        <div className="px-5 py-2.5 border-t border-[var(--color-border)] bg-[var(--color-muted)]/20 flex items-center justify-between">
          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            Showing {checkIns?.length} unique check-in{checkIns?.length !== 1 ? "s" : ""} today
          </span>
          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            Click refresh to update
          </span>
        </div>
      )}
    </div>
  );
}
